/**
 * Maintenance state — polled so the banner appears (and the lockout lifts)
 * without the user ever hitting reload.
 *
 * BE: ``GET /api/v1/maintenance`` (public, allow-listed in the maintenance
 * middleware — a client must be able to learn WHY it is being refused).
 *
 * Three states, no half-measures:
 *   off       → nothing rendered
 *   scheduled → dismissible pre-announcement banner
 *   active    → blocking full-screen takeover
 */

import { apiClient } from "@ando/core";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

export type MaintenanceState = "off" | "scheduled" | "active";

export interface MaintenanceRecord {
  readonly state: MaintenanceState;
  /** Server-resolved state: a scheduled window auto-activates inside it. */
  readonly effective_state: MaintenanceState;
  readonly message: string | null;
  readonly starts_at: string | null;
  readonly ends_at: string | null;
  readonly status_page_url: string | null;
  /** True when THIS caller sails through the lockout (platform-admin role). */
  readonly bypassed?: boolean;
}

export const maintenanceQueryKey = ["maintenance", "status"] as const;

const POLL_MS = 45_000;

export function useMaintenance() {
  const query = useQuery<MaintenanceRecord>({
    queryKey: maintenanceQueryKey,
    queryFn: ({ signal }) =>
      // Base-relative: ``apiClient`` already prefixes ``apiUrl``. The old
      // ``/api/v1/maintenance`` literal only worked on web because the base
      // there is the relative ``/api/v1`` (``startsWith`` escape hatch); on
      // desktop the base is absolute and it became ``/api/v1/api/v1/...`` → 404
      // on every 45 s poll in production (Sep 15, 2026).
      apiClient.get<MaintenanceRecord>("/maintenance", { signal }),
    // Polling is the floor; the 503 interceptor flips the store instantly.
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    // Never let a failed probe imply "maintenance" — fail open, stay usable.
    retry: 1,
    staleTime: 0,
  });

  const record = query.data;
  const state: MaintenanceState = record?.effective_state ?? "off";

  return {
    record,
    state,
    // An operator with bypass keeps the working app — showing them the takeover
    // over a live platform would be a lie, and they need it to verify.
    isBlocking: state === "active" && !record?.bypassed,
    isAnnounced: state === "scheduled",
    isOperatorBypass: state === "active" && Boolean(record?.bypassed),
  };
}

/** Live countdown to `iso`, ticking every second. Returns null once passed. */
export function useCountdown(iso: string | null | undefined): {
  hours: number;
  minutes: number;
  seconds: number;
  totalMs: number;
} | null {
  const target = iso ? new Date(iso).getTime() : null;
  const now = useTick(Boolean(target));

  if (!target || Number.isNaN(target)) return null;
  const totalMs = target - now;
  if (totalMs <= 0) return null;

  const totalSeconds = Math.floor(totalMs / 1000);
  return {
    hours: Math.floor(totalSeconds / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
    totalMs,
  };
}

/** Local 1s ticker — only the countdown re-renders, never the whole tree. */
function useTick(enabled: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [enabled]);
  return now;
}
